/** AWS groups GPU quotas by instance-class prefix, not per-family. */
export const AWS_QUOTA_GROUPS: Record<string, { label: string; families: string[] }> = {
  g_vt_instances: { label: 'G/VT Instances', families: ['g4dn', 'g4ad', 'g5', 'g5g'] },
  p_instances: { label: 'P Instances', families: ['p3', 'p3dn', 'p4d', 'p4de', 'p5'] },
};

export function getQuotaGroup(provider: string, family: string): { label: string; families: string[] } | null {
  if (provider !== 'aws') return null;
  for (const group of Object.values(AWS_QUOTA_GROUPS)) {
    if (group.families.includes(family)) return group;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Shared QuotaData interface — canonical shape from the backend
// ---------------------------------------------------------------------------
export interface EstimatedGPUCards {
  limit: number;
  usage: number;
  unit: string;       // 'estimated_gpus'
  raw_vcpus: number;
}

export interface QuotaData {
  provider: string;
  region: string;
  region_display_name?: string;
  instance_family: string;
  quota_limit: number;
  current_usage: number;
  utilization_percent: number;
  available: number;
  usage_trend: string;
  last_updated: string;
  alert_threshold: number;
  quota_increase_requested: boolean;
  estimated_gpu_cards?: EstimatedGPUCards;
}

// ---------------------------------------------------------------------------
// QuotaGroup — aggregate view of families sharing one AWS Service Quota
// ---------------------------------------------------------------------------
export interface QuotaGroup {
  groupKey: string;            // e.g. 'g_vt_instances'
  groupLabel: string;          // e.g. 'G/VT Instances'
  families: QuotaData[];       // individual family records, sorted by usage desc
  quota_limit: number;         // the shared limit (same across all families)
  total_usage: number;         // sum of all families' current_usage
  total_available: number;     // limit - total_usage
  utilization_percent: number; // total_usage / limit * 100
  region: string;
  region_display_name?: string;
  provider: string;
  usage_trend: string;
  last_updated: string;
  alert_threshold: number;
  quota_increase_requested: boolean;
}

// ---------------------------------------------------------------------------
// groupSharedQuotas — coalesces AWS families into QuotaGroup aggregates
// ---------------------------------------------------------------------------
export function groupSharedQuotas(
  provider: string,
  regionQuotas: QuotaData[],
): { groups: QuotaGroup[]; ungrouped: QuotaData[] } {
  if (provider !== 'aws') {
    return { groups: [], ungrouped: regionQuotas };
  }

  const groupEntries: Record<string, { key: string; label: string; families: QuotaData[] }> = {};
  const ungrouped: QuotaData[] = [];

  for (const q of regionQuotas) {
    let matched = false;
    for (const [key, def] of Object.entries(AWS_QUOTA_GROUPS)) {
      if (def.families.includes(q.instance_family)) {
        if (groupEntries[key]) {
          groupEntries[key].families.push(q);
        } else {
          groupEntries[key] = { key, label: def.label, families: [q] };
        }
        matched = true;
        break;
      }
    }
    if (!matched) {
      ungrouped.push(q);
    }
  }

  const groups: QuotaGroup[] = [];
  for (const entry of Object.values(groupEntries)) {
    const families: QuotaData[] = entry.families;
    // Sort families by current_usage descending
    families.sort((a: QuotaData, b: QuotaData) => b.current_usage - a.current_usage);

    const limit = families[0].quota_limit; // shared limit
    const totalUsage = families.reduce((sum: number, f: QuotaData) => sum + f.current_usage, 0);
    const totalAvailable = Math.max(0, limit - totalUsage);
    const utilPct = limit > 0 ? (totalUsage / limit) * 100 : 0;

    // Pick the most recent last_updated across families
    const mostRecent = families.reduce((latest: QuotaData, f: QuotaData) =>
      new Date(f.last_updated) > new Date(latest.last_updated) ? f : latest,
    );

    // Aggregate trend: if any family is increasing, group is increasing
    const trend = families.some((f: QuotaData) => f.usage_trend === 'increasing')
      ? 'increasing'
      : families.some((f: QuotaData) => f.usage_trend === 'decreasing')
        ? 'decreasing'
        : 'stable';

    groups.push({
      groupKey: entry.key,
      groupLabel: entry.label,
      families,
      quota_limit: limit,
      total_usage: totalUsage,
      total_available: totalAvailable,
      utilization_percent: utilPct,
      region: families[0].region,
      region_display_name: families[0].region_display_name,
      provider: families[0].provider,
      usage_trend: trend,
      last_updated: mostRecent.last_updated,
      alert_threshold: families[0].alert_threshold,
      quota_increase_requested: families.some((f: QuotaData) => f.quota_increase_requested),
    });
  }

  // Sort groups by utilization descending
  groups.sort((a, b) => b.utilization_percent - a.utilization_percent);

  return { groups, ungrouped };
}

// ---------------------------------------------------------------------------
// deduplicateForAggregation — collapses shared-group families into synthetic
// QuotaData entries so totals/averages don't double-count shared limits.
// ---------------------------------------------------------------------------
export function deduplicateForAggregation(quotas: QuotaData[]): QuotaData[] {
  // Separate AWS from non-AWS
  const awsQuotas = quotas.filter(q => q.provider === 'aws');
  const otherQuotas = quotas.filter(q => q.provider !== 'aws');

  if (awsQuotas.length === 0) return quotas;

  // Group AWS quotas by region, then by quota group
  const byRegion: Record<string, QuotaData[]> = {};
  for (const q of awsQuotas) {
    if (!byRegion[q.region]) {
      byRegion[q.region] = [];
    }
    byRegion[q.region].push(q);
  }

  const deduped: QuotaData[] = [];
  for (const regionQuotas of Object.values(byRegion)) {
    const { groups, ungrouped } = groupSharedQuotas('aws', regionQuotas);

    // Each group becomes one synthetic QuotaData
    for (const g of groups) {
      deduped.push({
        provider: g.provider,
        region: g.region,
        region_display_name: g.region_display_name,
        instance_family: g.groupLabel,
        quota_limit: g.quota_limit,
        current_usage: g.total_usage,
        utilization_percent: g.utilization_percent,
        available: g.total_available,
        usage_trend: g.usage_trend,
        last_updated: g.last_updated,
        alert_threshold: g.alert_threshold,
        quota_increase_requested: g.quota_increase_requested,
      });
    }

    deduped.push(...ungrouped);
  }

  return [...deduped, ...otherQuotas];
}

// ---------------------------------------------------------------------------
// Shared color palettes
// ---------------------------------------------------------------------------
export const familyColors: Record<string, { usage: string; available: string }> = {
  'g4dn': { usage: 'bg-blue-600', available: 'bg-blue-300' },
  'g4ad': { usage: 'bg-sky-600', available: 'bg-sky-300' },
  'g5': { usage: 'bg-green-600', available: 'bg-green-300' },
  'g5g': { usage: 'bg-orange-600', available: 'bg-orange-300' },
  'p3': { usage: 'bg-violet-600', available: 'bg-violet-300' },
  'p3dn': { usage: 'bg-fuchsia-600', available: 'bg-fuchsia-300' },
  'p4d': { usage: 'bg-purple-600', available: 'bg-purple-300' },
  'p4de': { usage: 'bg-pink-600', available: 'bg-pink-300' },
  'p5': { usage: 'bg-rose-600', available: 'bg-rose-300' },
  'a100': { usage: 'bg-red-600', available: 'bg-red-300' },
  'a100-80gb': { usage: 'bg-red-700', available: 'bg-red-400' },
  'v100': { usage: 'bg-yellow-600', available: 'bg-yellow-300' },
  't4': { usage: 'bg-indigo-600', available: 'bg-indigo-300' },
  'l4': { usage: 'bg-emerald-600', available: 'bg-emerald-300' },
  'h100': { usage: 'bg-cyan-600', available: 'bg-cyan-300' },
  'p100': { usage: 'bg-amber-600', available: 'bg-amber-300' },
  'k80': { usage: 'bg-slate-600', available: 'bg-slate-300' },
  // Azure GPU families
  'a100-nd': { usage: 'bg-purple-500', available: 'bg-purple-200' },
  'h100-nd': { usage: 'bg-rose-500', available: 'bg-rose-200' },
  'a10': { usage: 'bg-teal-600', available: 'bg-teal-300' },
  'nv-v3': { usage: 'bg-gray-500', available: 'bg-gray-300' },
  // Group-level entries (used by stacked bar after deduplication)
  'G/VT Instances': { usage: 'bg-blue-600', available: 'bg-blue-300' },
  'P Instances': { usage: 'bg-purple-600', available: 'bg-purple-300' },
};

export function getFamilyBarColor(family: string, type: 'usage' | 'available'): string {
  return familyColors[family]?.[type] || (type === 'usage' ? 'bg-gray-600' : 'bg-gray-400');
}

// ---------------------------------------------------------------------------
// Human-readable display names per provider/family
// AWS families are instance-class prefixes (g4dn, p4d) — already recognizable.
// GCP families are GPU model names (t4, a100) — already recognizable.
// Azure families need the VM series context to make sense.
// ---------------------------------------------------------------------------
const AZURE_FAMILY_DISPLAY: Record<string, string> = {
  't4':      'T4 (NC v3)',
  'a100':    'A100 (NC v4)',
  'a100-nd': 'A100 (ND v4)',
  'h100':    'H100 (NC v5)',
  'h100-nd': 'H100 (ND v5)',
  'a10':     'A10 (NV v5)',
  'nv-v3':   'M60 (NV v3)',
};

export function getFamilyDisplayName(provider: string, family: string): string {
  if (provider === 'azure') {
    return AZURE_FAMILY_DISPLAY[family] || family.toUpperCase();
  }
  return family.toUpperCase();
}

// ---------------------------------------------------------------------------
// Shared utility helpers (promoted from ProviderQuotaCard for reuse)
// ---------------------------------------------------------------------------
export function getUtilizationColor(percent: number): string {
  if (percent >= 90) return 'text-red-500 bg-red-500';
  if (percent >= 80) return 'text-yellow-500 bg-yellow-500';
  return 'text-green-500 bg-green-500';
}

export function getUtilizationBg(percent: number): string {
  if (percent >= 90) return 'bg-red-900/20 border-red-800';
  if (percent >= 80) return 'bg-yellow-900/20 border-yellow-800';
  return 'bg-gray-900 border-gray-800';
}

export function formatLastUpdated(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffMins < 1440) return `${Math.floor(diffMins / 60)} hours ago`;

  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function getDataFreshness(dateString: string): {
  color: string;
  bgColor: string;
  status: string;
  stale: boolean;
} {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 5)
    return { color: 'text-green-400', bgColor: 'bg-green-400/10 border-green-400/30', status: 'Fresh', stale: false };
  if (diffMins < 15)
    return { color: 'text-yellow-400', bgColor: 'bg-yellow-400/10 border-yellow-400/30', status: 'Recent', stale: false };
  if (diffMins < 60)
    return { color: 'text-orange-400', bgColor: 'bg-orange-400/10 border-orange-400/30', status: 'Stale', stale: true };
  return { color: 'text-red-400', bgColor: 'bg-red-400/10 border-red-400/30', status: 'Very Stale', stale: true };
}

// ---------------------------------------------------------------------------
// Format GPU card display string for AWS quotas
// ---------------------------------------------------------------------------
export function formatGPUCardDisplay(quota: QuotaData): string | null {
  if (!quota.estimated_gpu_cards) return null;
  const { limit, usage } = quota.estimated_gpu_cards;
  return `~${usage} / ${limit} GPUs`;
}
